import { useMemo, type ReactNode } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { IncidentListItem } from '@/app/services/incident/incident.service';
import { IncidentOmpSnapshotAcceptButton } from '@/app/components/incident-overview/IncidentOmpSnapshotAcceptButton';
import {
  computeOmpLossSoFar,
  formatOmpDurationLabel,
} from '@/app/components/incident-overview/utils/incident-omp.utils';
import { formatEuro } from '~/app/landing/sla/shared/utils/sla-format.utils';

const { Text } = Typography;

type Props = {
  incident: IncidentListItem;
};

type SnapshotPillProps = {
  label: string;
  value: string;
  variant:
    | 'revenue'
    | 'loss'
    | 'revenue-loss'
    | 'penalty'
    | 'oem'
    | 'recommended';
};

function SnapshotPill({ label, value, variant }: SnapshotPillProps) {
  return (
    <span
      className={`incident-omp-snapshot-pill incident-omp-snapshot-pill--${variant}`}
    >
      <span className="incident-omp-snapshot-pill__label">{label}</span>
      <span className="incident-omp-snapshot-pill__value">{value}</span>
    </span>
  );
}

function splitIntoTwoRows<T>(items: T[]): [T[], T[]] {
  const firstRowCount = Math.ceil(items.length / 2);

  return [items.slice(0, firstRowCount), items.slice(firstRowCount)];
}

export function IncidentOmpSnapshotRow({ incident }: Props) {
  const { t } = useTranslation('incidents');
  const omp = incident.omp;

  const pillRows = useMemo(() => {
    if (!omp) {
      return [[], []] as [ReactNode[], ReactNode[]];
    }

    const durationLabel = formatOmpDurationLabel(omp.openHours);
    const lossSoFar = computeOmpLossSoFar(omp);
    const recommendedProposal = omp.proposals.find(
      (proposal) => proposal.isRecommended,
    );
    const revenueWindowLabel =
      recommendedProposal?.interventionLabel ?? durationLabel;
    const penaltyDurationLabel =
      omp.completedPenaltyDays > 0
        ? `${omp.completedPenaltyDays}d`
        : durationLabel;

    const pills: ReactNode[] = [];

    if (omp.recommendedLabel) {
      pills.push(
        <SnapshotPill
          key="recommended"
          variant="recommended"
          label={t('overview.snapshot.recommendedOem', 'Recommended O&M')}
          value={omp.recommendedLabel}
        />,
      );
    }

    pills.push(
      <SnapshotPill
        key="revenue"
        variant="revenue"
        label={t('overview.snapshot.dailyRevenue', 'Daily revenue')}
        value={formatEuro(omp.dailyRevenueTotal)}
      />,
      <SnapshotPill
        key="loss"
        variant="loss"
        label={t('overview.snapshot.lossSoFar', {
          duration: durationLabel,
          defaultValue: 'Loss so far ({{duration}})',
        })}
        value={formatEuro(lossSoFar)}
      />,
      <SnapshotPill
        key="revenue-loss"
        variant="revenue-loss"
        label={t('overview.snapshot.revenueLoss', {
          duration: revenueWindowLabel,
          defaultValue: 'Revenue loss ({{duration}})',
        })}
        value={formatEuro(omp.revenueLoss)}
      />,
      <SnapshotPill
        key="penalty"
        variant="penalty"
        label={t('overview.snapshot.slaPenalty', {
          duration: penaltyDurationLabel,
          defaultValue: 'SLA penalty ({{duration}})',
        })}
        value={formatEuro(omp.slaPenaltyAccrued)}
      />,
      <SnapshotPill
        key="oem"
        variant="oem"
        label={t('overview.snapshot.oemCost', 'O&M cost')}
        value={formatEuro(omp.oemCost)}
      />,
    );

    return splitIntoTwoRows(pills);
  }, [omp, t]);

  if (!omp) {
    return null;
  }

  const [firstRow, secondRow] = pillRows;

  return (
    <div className="incident-omp-snapshot">
      <Text type="secondary" className="incident-omp-snapshot__title">
        {t('overview.snapshot.title', 'Best recommendation')}
      </Text>

      <div className="incident-omp-snapshot__cluster">
        <div className="incident-omp-snapshot__lines">
          <div className="incident-omp-snapshot__line-row">{firstRow}</div>
          <div className="incident-omp-snapshot__line-row">{secondRow}</div>
        </div>

        <div className="incident-omp-snapshot__accept-slot">
          <IncidentOmpSnapshotAcceptButton />
        </div>
      </div>
    </div>
  );
}
